import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

interface NewCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { name: string; description: string | null }) => Promise<void> | void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

const TITLE_ID = 'new-collection-title';

export const NewCollectionModal: React.FC<NewCollectionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage = null,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setLocalError(null);
      requestAnimationFrame(() => nameRef.current?.focus());
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setLocalError('Give your collection a name.');
      nameRef.current?.focus();
      return;
    }
    setLocalError(null);
    await onSubmit({ name: trimmed, description: description.trim() || null });
  };

  const shownError = localError ?? errorMessage;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isBusy={isSubmitting} labelledBy={TITLE_ID}>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header
          title="Create a collection"
          titleId={TITLE_ID}
          subtitle="Group related notes under a shared binder. You can add notes now or later."
        />

        <Modal.Body>
          <label className="pt-4 pb-modal-field">
            <span className="pb-modal-label">Name</span>
            <input
              ref={nameRef}
              type="text"
              className="pb-modal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="e.g. Field journal, Prototype ideas"
              disabled={isSubmitting}
              autoComplete="off"
            />
          </label>

          <label className="pb-modal-field">
            <span className="pb-modal-label">
              Description <span className="pb-modal-label__optional">optional</span>
            </span>
            <textarea
              className="pb-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="What belongs here?"
              disabled={isSubmitting}
            />
          </label>

          {shownError && <p className="pb-modal-error" role="alert">{shownError}</p>}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="pb-modal-btn pb-modal-btn--ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pb-modal-btn pb-modal-btn--primary"
            disabled={isSubmitting || !name.trim()}
          >
            {isSubmitting ? 'Creating…' : 'Continue'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
